import { set } from '@ember/object';
import { trackedArray } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import type { Task } from 'ember-concurrency';
import type { Hotel, Ota } from 'frontend-utils/models';
import {
  type Platform,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import type {
  Day,
  Period,
  RoomTypeRecommendation,
  ShortTermRentalOccupancy,
  ShortTermRentalRate,
} from 'lumen/models';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { PA_REF_DAYS_AGO } from 'lumen/utils/constants/pricing-assistant-constants';
import { DETAIL_TABS } from 'lumen/utils/constants/rate-constants';
import { mapRateParamToAPIParams } from 'lumen/utils/rates-request';
import { hash } from 'rsvp';

// Types may be incorrect
type MarketMarketratesParams = {
  brands: string;
  compareType: string;
  compareValue: string;
  day: string;
  expandedRowIds: string;
  los: string;
  managementTypes: string;
  maxPersons: string;
  mealPlan: string;
  membershipType: string;
  otaId: string;
  page: string;
  platform: Platform;
  rateParam: string;
  roomType: string;
  view: string;
};

type Model = {
  day: Day;
  hotel: Hotel;
  loading: boolean;
  periods: Period[];
  periodsLastYear: Period[];
  periodsOTB: Period[];
  pricing: {
    roomTypeRecommendations: RoomTypeRecommendation[];
  };
  ratesAndRecommendationsSingleDayResponseTask?: Task<unknown, unknown[]>;
  rental: {
    occupancies: ShortTermRentalOccupancy[];
    rates: ShortTermRentalRate[];
  };
  roomTypeRecommendationsResponseTask?: Task<unknown, unknown[]>;
};

// Types may be incorrect
type Params = {
  date: string;
  hotelId: string;
  offset: number;
  type: string;
};

export default class MarketMarketratesDetailRoute extends Route {
  queryParams = {
    date: {
      refreshModel: true,
    },
    hotelId: {
      refreshModel: true,
    },
    offset: {
      refreshModel: true,
    },
    type: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private additionalParams(queryParams: Partial<Params>) {
    const params = this.paramsFor(
      'market.marketrates',
    ) as Partial<MarketMarketratesParams>;
    const hotelId = queryParams.hotelId;

    return {
      ...params,
      hotelId,
    };
  }

  beforeModel(transition: Transition): void {
    const routeName = transition.to.name;
    const queryParams = transition.to.queryParams as Partial<Params>;
    const data = this.additionalParams(queryParams);

    const date = queryParams['date'];
    const detailTab = queryParams['type'];
    const otaId = data['otaId'];

    // We do an extra check if otaId exists to prevent peeking null records (for example: RI without rates tab would crash without this check)
    if (otaId) {
      // @ts-expect-error: Incorrect type
      const hasBookingCurve = this.access.hasBookingCurveOnDate(date);
      const { hasRatesEvolution } = this.store.peekRecord<Ota>('ota', otaId)!;

      if (!hasBookingCurve && !hasRatesEvolution) {
        this.transition.redirectToEnabledTab(transition);
      } else if (
        (!detailTab && hasRatesEvolution) ||
        (detailTab === DETAIL_TABS.BOOKING_CURVE && !hasBookingCurve) ||
        detailTab === DETAIL_TABS.PRICING
      ) {
        transition.abort();
        queryParams['type'] = DETAIL_TABS.RATE_EVOLUTION;

        this.router.transitionTo(
          routeName,
          this.hotelAndMarketService.hotelOrMarket,
          {
            queryParams,
          },
        );
      } else if (
        (!detailTab && hasBookingCurve) ||
        (detailTab === DETAIL_TABS.RATE_EVOLUTION && !hasRatesEvolution) ||
        detailTab === DETAIL_TABS.PRICING
      ) {
        transition.abort();
        queryParams['type'] = DETAIL_TABS.BOOKING_CURVE;

        this.router.transitionTo(
          routeName,
          this.hotelAndMarketService.hotelOrMarket,
          {
            queryParams,
          },
        );
      }
    }

    if (date === undefined) {
      // Without a date we can't peek the day record in the store
      this.transition.redirectToEnabledTab(transition);
    }
  }

  model(params: Params): Model {
    const { date, offset, type: detailTab } = params;
    const data = this.additionalParams(params);

    const model: Model = {
      // @ts-expect-error: Incorrect type
      day: undefined,
      hotel: {
        // @ts-expect-error: Incorrect type
        loading: true,
      },
      loading: true,
      periods: [],
      periodsLastYear: [],
      periodsOTB: trackedArray<Period>(),
      pricing: {
        roomTypeRecommendations: [],
      },
      rental: {
        occupancies: [],
        rates: [],
      },
    };

    const hotelId = data['hotelId']!;
    const hotel = this.store.peekRecord<Hotel>('hotel', hotelId);

    if (hotel) {
      set(model, 'hotel', hotel);
      set(model, 'hotel.loading', false);
    } else {
      this.store
        .query<Hotel>('hotel', { id: hotelId }, { backgroundReload: false })
        .then(
          (hotels) => {
            set(model, 'hotel', hotels[0]!);
            set(model, 'hotel.loading', false);
          },
          () => {
            // Do nothing
          },
        );
    }

    const sharedRatesEvolutionParams = {
      change_days: '',
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      compset_ids: data.compsetId,
      from_date: date,
      hotel_id: hotelId,
      los: data.los,
      mealtype: data.mealPlan,
      membershiptype: data.membershipType,
      meta: 'nested',
      namespace: APIGATEWAY,
      nr_days: 31, // TODO: Deprecate
      nr_of_days: 31,
      offset,
      ota: data.otaId,
      persons: data.maxPersons,
      platform: data.platform,
      roomtype: data.roomType,
      typeKey: 'rates/historical',
    };

    const sharedOccupancyParams = {
      from_date: date,
      namespace: APIGATEWAY,
      nr_of_days: 365,
      offset,
      subscription_id: hotelId,
      typeKey: 'revenueinsight/occupancies/evolutions',
    };

    const sharedShortTermRentalParams = {
      from_date: date,
      hotel_id: hotelId,
      namespace: APIGATEWAY,
      offset,
      typeKey: 'shorttermrental/fromdates/evolutions',
      use_filter: 'default',
    };

    const hasBookingCurveAccess =
      this.access.hasFeature('pms.otb.view') ||
      (this.access.hasShortTermRentalAccess &&
        !this.access.hasShortTermRentalOnlyStrTab);

    if (detailTab === DETAIL_TABS.PRICING) {
      const requestPromises = [];

      // We don't show rooms tab in case
      // PMS is not integrated
      // OR its an open pricing subscription
      if (
        this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated &&
        !this.access.hasPricingAssistantOpenPricingFeature
      ) {
        // Fetch room type recommendations
        model.roomTypeRecommendationsResponseTask =
          this.pricingAssistantDataRequestService.pricingAssistantRoomTypeRecommendationsTask;

        requestPromises.push(
          model.roomTypeRecommendationsResponseTask.perform(
            hotelId,
            date,
            date,
          ),
        );
      }

      const prefetchedDay = this.store.peekRecord<Day>('day', date);

      // Check if any of prefetched day periods has carates
      const prefetchedPeriodWithCarates = prefetchedDay?.periods?.find(
        (period) => period.carates.length,
      );

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const monthDayjs = dayjs(data.month);
      const isDayInCurrentMonth = dayjs(date).isSame(monthDayjs, 'month');

      // Use prefetched data for days in current month, otherwise fetch the data from the API
      if (prefetchedDay && prefetchedPeriodWithCarates && isDayInCurrentMonth) {
        // request reasons each day for open pricing
        const prefetchedRecommendationReasonsExist =
          prefetchedPeriodWithCarates.carates.find(
            (carate) =>
              carate.carecommendation &&
              carate.carecommendation.paRecommendationReason,
          );

        if (
          !prefetchedRecommendationReasonsExist &&
          this.access.hasPricingAssistantOpenPricingFeature
        ) {
          requestPromises.push(
            this.pricingAssistantDataRequestService.pricingAssistantRecommendationReasonsRequestTask.perform(
              hotelId,
              date,
              date,
            ),
          );
        }

        set(model, 'periods', [...model.periods, prefetchedPeriodWithCarates]);
      } else {
        const compareDate = this.hotelAndMarketService
          .getLocalTime()
          .subtract(PA_REF_DAYS_AGO, 'day')
          .format('YYYY-MM-DD');

        // Request PA rates and recommendations
        model.ratesAndRecommendationsSingleDayResponseTask =
          this.pricingAssistantDataRequestService.pricingAssistantRatesAndRecommendationsSingleDayRequestTask;

        requestPromises.push(
          model.ratesAndRecommendationsSingleDayResponseTask.perform(
            hotelId,
            date,
            compareDate,
          ),
        );

        // Request recommendation reasons
        requestPromises.push(
          this.pricingAssistantDataRequestService.pricingAssistantRecommendationReasonsRequestTask.perform(
            hotelId,
            date,
            date,
          ),
        );

        // Request day holidays
        requestPromises.push(
          this.store.query<Day>('day', {
            from_date_range_end: date,
            from_date_range_start: date,
            hotel_id: hotelId,
            namespace: APIGATEWAY,
            typeKey: 'holidays',
          }),
        );

        // Request day accepted events
        requestPromises.push(
          this.store.query<Day>('day', {
            from_date_range_end: date,
            from_date_range_start: date,
            hotel_id: hotelId,
            include_distance: true,
            namespace: APIGATEWAY,
            typeKey: 'events/accepted',
          }),
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      Promise.all(requestPromises).then(() => {
        if (
          !model.ratesAndRecommendationsSingleDayResponseTask?.isRunning &&
          model.ratesAndRecommendationsSingleDayResponseTask?.last?.value
        ) {
          // The task resolves two promises, the first one is recommendation and the second one is it respective period.
          // Extract the period from the response and add it to the model
          // @ts-expect-error: Incorrect type
          const [, period] =
            model.ratesAndRecommendationsSingleDayResponseTask.last.value;

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          set(model, 'periods', [...model.periods, ...period]);
        }

        if (
          !model.roomTypeRecommendationsResponseTask?.isRunning &&
          model.roomTypeRecommendationsResponseTask?.last?.value
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          model.pricing.roomTypeRecommendations = [
            ...model.pricing.roomTypeRecommendations,
            // @ts-expect-error: Incorrect type
            ...model.roomTypeRecommendationsResponseTask.last.value,
          ];
        }

        set(model, 'loading', false);
      });
    } else if (detailTab === DETAIL_TABS.RATE_EVOLUTION) {
      // Request when the rates evolution curve is active
      // @ts-expect-error: Incorrect type
      mapRateParamToAPIParams(data.rateParam, sharedRatesEvolutionParams);

      // The rates/historical response is capped at today, revenueinsight/occcupancies and shorttermrental/fromdates/evolutions are capped at from_date
      // We need to use (from_date - today) as offset in case from_date > today so both response contain the same periods
      const localToday = this.hotelAndMarketService.getLocalTime();
      const localDate = this.hotelAndMarketService.toLocalTime(date);
      const dayDifference = localToday.diff(localDate, 'day', true);
      const customOffset =
        dayDifference < 0
          ? Math.abs(Math.ceil(dayDifference)) + 1 + offset
          : offset;

      if (
        data.platform !== null &&
        data.platform !== undefined &&
        data.platform > PLATFORMS.PLATFORM_DESKTOP
      ) {
        // @ts-expect-error: Incorrect type
        sharedRatesEvolutionParams.allow_labels = ['no_label', 'mobile_only'];
      }

      const requestHash = {
        periods: this.store.query<Period>('period', sharedRatesEvolutionParams),
        periodsOTB: undefined,
        rentalData: undefined,
      } as {
        periods: Promise<Period[]>;
        periodsOTB: Promise<Period[]> | undefined;
        rentalData: Promise<ShortTermRentalRate[]> | undefined;
      };

      // Make OTB data request ONLY when feature is available
      if (this.access.hasFeature('pms.otb.view')) {
        requestHash.periodsOTB = this.store.query<Period>('period', {
          ...sharedOccupancyParams,
          nr_of_days: sharedRatesEvolutionParams.nr_of_days,
          offset: customOffset,
        });
      }

      if (
        this.access.hasShortTermRentalAccess &&
        !this.access.hasShortTermRentalOnlyStrTab
      ) {
        requestHash.rentalData = this.store.query<ShortTermRentalRate>(
          'short-term-rental-rate',
          {
            ...sharedShortTermRentalParams,
            nr_of_days: sharedRatesEvolutionParams.nr_of_days,
            offset: customOffset,
            use_filter: 'default',
          },
        );
      }

      hash(requestHash).then((hashResult) => {
        const { periods, periodsOTB, rentalData } = hashResult;

        set(model, 'periods', [...model.periods, ...periods]);

        if (periodsOTB) {
          model.periodsOTB.push(...periodsOTB);
        }

        if (rentalData) {
          set(
            model,
            'rental.rates',
            // rentalData contains both short-term-rental-rater and short-term-rental-occupancies
            rentalData.filter(
              (item) => item.modelName === 'short-term-rental-rate',
            ),
          );
        }

        set(model, 'loading', false);
      });
    } else if (
      detailTab === DETAIL_TABS.BOOKING_CURVE &&
      hasBookingCurveAccess
    ) {
      // Gets the same weekday one year ago (https://en.wikipedia.org/wiki/ISO_week_date)
      const dateLastYear = dayjs
        .utc(date)
        .subtract(52, 'week')
        .format('YYYY-MM-DD');

      // If we have OTB data we fetch the periods from the OTB request, otherwise we fetch them from the Short-term rental request
      const periodParams = this.access.hasFeature('pms.otb.view')
        ? sharedOccupancyParams
        : sharedShortTermRentalParams;

      const requestHash = {
        periods: this.store.query<Period>('period', {
          ...periodParams,
          nr_of_days: sharedOccupancyParams.nr_of_days,
        }),
        periodsLastYear: this.store.query<Period>('period', {
          ...periodParams,
          from_date: dateLastYear,
          nr_of_days: sharedOccupancyParams.nr_of_days,
        }),
        rentalOccupancies: undefined,
        rentalOccupanciesLastYear: undefined,
      } as {
        periods: Promise<Period[]>;
        periodsLastYear: Promise<Period[]>;
        rentalOccupancies: Promise<ShortTermRentalOccupancy[]> | undefined;
        rentalOccupanciesLastYear:
          | Promise<ShortTermRentalOccupancy[]>
          | undefined;
      };

      if (
        this.access.hasShortTermRentalAccess &&
        !this.access.hasShortTermRentalOnlyStrTab
      ) {
        requestHash.rentalOccupancies =
          this.store.query<ShortTermRentalOccupancy>(
            'short-term-rental-occupancy',
            {
              ...sharedShortTermRentalParams,
              nr_of_days: sharedOccupancyParams.nr_of_days,
              use_filter: 'default',
            },
          );

        requestHash.rentalOccupanciesLastYear =
          this.store.query<ShortTermRentalOccupancy>(
            'short-term-rental-occupancy',
            {
              ...sharedShortTermRentalParams,
              from_date: dateLastYear,
              nr_of_days: sharedOccupancyParams.nr_of_days,
              use_filter: 'default',
            },
          );
      }

      hash(requestHash).then((hashResult) => {
        const {
          periods,
          periodsLastYear,
          rentalOccupancies,
          rentalOccupanciesLastYear,
        } = hashResult;

        // Create a map per extractdate so we can match OTB data via a lookup in O(1)
        const lastYearPeriodsByExtractDate: Record<string, Period> = {};

        periodsLastYear.forEach((periodLastYear) => {
          lastYearPeriodsByExtractDate[periodLastYear.formattedExtractDate] =
            periodLastYear;
        });

        periods.forEach((period) => {
          // @ts-expect-error: Incorrect type
          period.lastYear =
            lastYearPeriodsByExtractDate[period.formattedExtractDateLastYear];
        });

        if (rentalOccupancies && rentalOccupanciesLastYear) {
          const rentalDataByExtractDate: Record<
            string,
            ShortTermRentalOccupancy
          > = {};

          rentalOccupanciesLastYear.forEach((rentalOccupancyLastYear) => {
            rentalDataByExtractDate[
              rentalOccupancyLastYear.formattedExtractDate
            ] = rentalOccupancyLastYear;
          });

          rentalOccupancies.forEach((rentalOccupancy) => {
            // @ts-expect-error: Incorrect type
            rentalOccupancy.lastYear =
              rentalDataByExtractDate[
                rentalOccupancy.formattedExtractDateLastYear
              ];
          });

          model.rental.occupancies = [
            ...model.rental.occupancies,
            ...rentalOccupancies,
          ];
        }

        set(model, 'periods', [...model.periods, ...periods]);
        set(model, 'periodsLastYear', [
          ...model.periodsLastYear,
          ...periodsLastYear,
        ]);
        set(model, 'loading', false);
      });
    } else {
      set(model, 'loading', false);
    }

    set(model, 'day', this.store.peekRecord<Day>('day', date));

    return model;
  }
}
